/**
 * FILE: ads-chatbot.model.ts
 * CHỨC NĂNG: Model, dữ liệu và helper chatbot dùng chung cho Ads và Vpage
 * TÁC DỤNG: Danh sách kênh tạo bot, chatbot mẫu + phân loại, link hướng dẫn
 */
import {ISelectOption} from '@core/models';
import {BotItem} from '@shared/models/chatbot/script/chatbot.model';
import {CHANNEL, getChannelLogo} from '@shared/models/conversation';
import {normalizeChannelValue} from '@shared/utils';

/** URL module chatbot theo dạng /{module}/{controller}/{action} của Ads */
export const ADS_CHATBOT_BASE_PATH = 'chatbot/script';

/** Kênh được phép tạo chatbot — giống bước chọn nền tảng của Vpage */
export const ADS_CHATBOT_CHANNELS: readonly number[] = [
  CHANNEL.FACEBOOK,
  CHANNEL.ZALO_PERSONAL,
  CHANNEL.ZALO,
  CHANNEL.INSTAGRAM,
  CHANNEL.SHOPEE,
];

/** Chatbot mẫu (BE: index ads.chatbot_template, admin quản lý ở /admin/chatbottemplate): dùng mẫu = tạo bot với cloneBotId = botId */
export interface IChatbotTemplate {
  id: string;
  botId: string;
  name: string;
  /** IChatbotTemplateCategory.id */
  categoryId: string;
  channel: number | string;
  description?: string;
  image?: string;
  link?: string;
  sort?: number;
  status?: number;
}

/** Phân loại chatbot mẫu (BE: index ads.chatbot_template_category, admin thêm/sửa ở /admin/chatbottemplate) */
export interface IChatbotTemplateCategory {
  id: string;
  name: string;
  description?: string;
  /** Class icon Phosphor, vd "ph ph-pill" */
  icon?: string;
  order?: number;
  status?: number;
  /** Số mẫu đang bật (chỉ có ở API Ads /ads/chatbot/templatecategories) */
  countNumber?: number;
}

/** Trạng thái dùng chung cho mẫu và phân loại */
export const CHATBOT_TEMPLATE_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
} as const;

/** Icon phân loại khi admin chưa nhập */
export const CHATBOT_TEMPLATE_DEFAULT_ICON = 'ph ph-robot';

/** Link hướng dẫn sử dụng kịch bản */
export const ADS_CHATBOT_MANUAL_LINK = 'https://vpage.manual.nhanh.vn/tinh-nang/chatbot/khoi-tao/kich-ban';

/** Danh sách fanpageId của bot (API trả string hoặc mảng) */
export function getBotFanpageIds(bot: BotItem | null | undefined): string[] {
  const ids = bot?.fanpageId as string[] | string | undefined;
  return (Array.isArray(ids) ? ids : ids ? [ids] : []).filter(Boolean);
}

/** Icon kênh của bot cho option d-select; imgCdn: transform của ImgCdnPipe */
export function getBotChannelIcon(channel: number | string | null | undefined, imgCdn: (path: string) => string): ISelectOption['iconLeft'] {
  switch (normalizeChannelValue(channel)) {
    case CHANNEL.FACEBOOK:
      return {classFont: 'ph-fill ph-facebook-logo text-primary fs-6'};
    case CHANNEL.INSTAGRAM:
      return {classFont: '', imageSrc: imgCdn(getChannelLogo(CHANNEL.INSTAGRAM))};
    case CHANNEL.ZALO:
      return {classFont: '', imageSrc: imgCdn(getChannelLogo(CHANNEL.ZALO))};
    case CHANNEL.ZALO_PERSONAL:
      return {classFont: '', imageSrc: imgCdn('logo/zalo_logo.png')};
    case CHANNEL.SHOPEE:
      return {classFont: '', imageSrc: imgCdn('logo/shopee_logo.png')};
    case CHANNEL.TIKTOK:
      return {classFont: '', imageSrc: imgCdn('logo/tiktok_shop.svg')};
    case CHANNEL.TIKTOK_BUSINESS:
      return {classFont: '', imageSrc: imgCdn('logo/tiktok.svg')};
    default:
      return {classFont: 'ph ph-robot'};
  }
}

export const ADS_CHATBOT_ICON_TONES = [
  'bg-danger bg-opacity-10 text-danger',
  'bg-purple bg-opacity-10 text-purple',
  'bg-warning bg-opacity-10 text-warning',
  'bg-success bg-opacity-10 text-success',
  'bg-primary bg-opacity-10 text-primary',
  'bg-secondary bg-opacity-10 text-secondary',
] as const;

export interface AdsChatbotCardType {
  type: string;
  title?: string;
  /** Text gốc giống nhau VI/EN — hiển thị thẳng, không dịch */
  originTitle?: string;
  question: string;
  channel: readonly number[];
}

/** Message types supported by the existing card builder and API. */
export const ADS_CHATBOT_CARD_TYPES: readonly AdsChatbotCardType[] = [
  {type: 'text', title: 'feature.chatbot.textCardSubtitle', question: 'feature.chatbot.textCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'quick_reply', title: 'feature.chatbot.quickReplyCardSubtitle', question: 'feature.chatbot.quickReplyCardSubtitle', channel: [CHANNEL.FACEBOOK]},
  {type: 'image', title: 'feature.chatbot.imageCardSubtitle', question: 'feature.chatbot.imageCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  // Tạm ẩn: Ads chưa có GenAI
  // {type: 'ai_chat', title: 'Chatbot AI (ChatGPT, Gemini, DeepSeek...)', question: 'feature.chatbot.aiAssistant', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'image_text', title: 'feature.chatbot.imageTextCardSubtitle', question: 'feature.chatbot.imageTextCardQuestion', channel: [CHANNEL.ZALO]},
  {type: 'gallery', title: 'feature.chatbot.galleryCardSubtitle', question: 'feature.chatbot.galleryCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'form', title: 'conversation.chatInput.requireAddress', question: 'feature.chatbot.formCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO]},
  {type: 'file', title: 'feature.chatbot.fileCardSubtitle', question: 'feature.chatbot.fileCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.INSTAGRAM]},
  {type: 'video', title: 'feature.chatbot.videoCardSubtitle', question: 'feature.chatbot.videoCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO_PERSONAL, CHANNEL.INSTAGRAM]},
  {type: 'audio', title: 'feature.chatbot.audioCardSubtitle', question: 'feature.chatbot.audioCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM]},
  {type: 'request_feedback', title: 'feature.chatbot.feedbackCardSubtitle', question: 'feature.chatbot.feedbackCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'marketing', title: 'feature.chatbot.marketingCardSubtitle', question: 'feature.chatbot.marketingCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'product_catalog', title: 'feature.chatbot.productCatalogCardTitle', question: 'feature.chatbot.productCatalogCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'api_nhanh', originTitle: 'API Nhanh.vn', question: 'feature.chatbot.apiNhanhCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'product_ecom', title: 'feature.chatbot.sendProduct', question: 'feature.chatbot.sendProduct', channel: [CHANNEL.SHOPEE]},
  {type: 'voucher_ecom', title: 'feature.chatbot.sendDiscountCode', question: 'feature.chatbot.sendDiscountCode', channel: [CHANNEL.SHOPEE]},
  {type: 'api', originTitle: 'External API', question: 'feature.chatbot.callExternalApi', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'send_bank_card', title: 'feature.chatbot.sendBankTransfer', question: 'feature.chatbot.sendBankCardQuestion', channel: [CHANNEL.FACEBOOK]},
];

/** Màu icon cố định theo loại card (vị trí trong ADS_CHATBOT_CARD_TYPES) — bước "Thêm bước" và node sơ đồ cùng màu */
export function getAdsCardTone(type: string): string {
  const index = Math.max(ADS_CHATBOT_CARD_TYPES.findIndex(item => item.type === type), 0);
  return ADS_CHATBOT_ICON_TONES[index % ADS_CHATBOT_ICON_TONES.length];
}

export interface AdsChatbotAddCardRequest {
  id: number;
  blockId: string;
  type: string;
}

/** Tài khoản ngân hàng của DN (/ads/chatbot/bankaccount) — cùng dữ liệu tài khoản ngân hàng của Vpage */
export interface IAdsBankAccount {
  id: string;
  bankCode: string;
  binCode: string;
  accountNumber: string;
  accountName: string;
}

export type IAdsBankAccountForm = Omit<IAdsBankAccount, 'id'>;
